import { navigate } from "@reach/router";
import {
  FormEvent,
  FunctionComponent,
  useContext,
  useRef,
  useState
} from "react";
import Button from "../../components/button/Button";
import Input, { InputValidation, TextArea } from "../../components/input/Input";
import ToolTip from "../../components/ToolTip/ToolTip";
import { IssuerCTSConfigData } from "../../utils/helpers/types";
import Switch from "../../components/switch/Switch";
import { resources } from "../../utils/resources";
import LayoutContext from "../../context/layout-context";
import { addIssuerOnCTS } from "../../utils/helpers/data/card-tokenization";
import { alphaNumericValidation } from "../../utils/helpers/validation-helpers";
import { PartialLoose } from "../../utils/helpers/types-helpers";

// interface ExtraField {
//   name: string;
//   value: string;
// }

const initialFormData: IssuerCTSConfigData = {
  issuerId: "",
  issuerName: "",
  bankCode: "",
  sendOtpOnBehalfOfIssuer: true,
  sendOtpEndpoint: "",
  idvOnExternalWalletsOnly: false,
  walletProviderId: "",
  encryptionSecret: "",
  maxVirtualCardProvisioning: 10,
  sendOtpAdditionalRequestParam: "",
  otpMessageTemplate: ""
};

const validations: PartialLoose<IssuerCTSConfigData, InputValidation> = {
  issuerId: {
    maxLength: 10,
    required: true
  },
  issuerName: {
    maxLength: 32,
    transform: alphaNumericValidation,
    required: true
  },
  bankCode: {
    maxLength: 32,
    required: true
  },
  encryptionSecret: {
    maxLength: 32,
    required: true
  },
  sendOtpEndpoint: {
    maxLength: 255
  },
  walletProviderId: {
    required: true,
    maxLength: 64
  },
  sendOtpAdditionalRequestParam: {
    maxLength: 255
  },
  maxVirtualCardProvisioning: {
    required: true
  }
};

const OnboardIssuerPage: FunctionComponent = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);

  const { notify } = useContext(LayoutContext);

  const templateRef = useRef<HTMLInputElement>(null);

  const handleChange = (key: keyof IssuerCTSConfigData, value: any) => {
    setFormData({ ...formData, [key]: value });
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const paramsObj = JSON.parse(
        formData.sendOtpAdditionalRequestParam || "{}"
      );
      if (paramsObj.constructor !== Object) {
        throw Error();
      }
    } catch (error) {
      notify(
        "error",
        "Error Parsing OTP Parameters. Please enter a valid JSON object or leave the field empty"
      );
      return;
    }
    setLoading(true);
    const response = await addIssuerOnCTS({
      ...formData,
      sendOtpEndpoint: formData.sendOtpOnBehalfOfIssuer
        ? ""
        : formData.sendOtpEndpoint
    });
    setLoading(false);
    if (response.error) {
      notify("error", String(response.message));
      return;
    }
    notify("success", "Issuer configured succesfully");
    setFormData(initialFormData);
  };

  const insertShortCode = () => {
    handleChange("otpMessageTemplate", `${formData.otpMessageTemplate} <<>> `);
    setTimeout(() => {
      templateRef.current?.focus();
      templateRef.current?.setSelectionRange(1000, 1000);
    }, 100);
  };

  return (
    <main className="content-layout">
      <form onSubmit={handleSave} className="generic-form cts-form">
        <div className="input-group">
          <span className="question">
            <span className="required">Issuer ID</span>
            <ToolTip
              toolTip="Issuer issuerId generated by the Interswitch Tokenization gateway"
              anchor={
                <img
                  alt="info tooltip"
                  src={resources.icons.INFO_CIRCLE_SOLID}
                  className="generic-icon small"
                />
              }
            />
          </span>
          <Input
            value={formData.issuerId}
            onChange={value => handleChange("issuerId", value)}
            placeholder="Enter issuer ID"
            inputValidation={validations.issuerId}
            id="ctsIssuerId"
          />
        </div>

        <div className="input-group">
          <span className="question">
            <span className="required">Issuer Name</span>
          </span>
          <Input
            value={formData.issuerName}
            onChange={value => handleChange("issuerName", value)}
            placeholder="Enter issuer name"
            inputValidation={validations.issuerName}
            id="ctsIssuerName"
          />
        </div>

        <div className="input-group">
          <span className="question">
            <span className="required">Issuer Bank code</span>
          </span>
          <Input
            value={formData.bankCode}
            onChange={value => handleChange("bankCode", value)}
            placeholder="Enter issuer bank code"
            inputValidation={validations.bankCode}
            id="ctsIssuerBankCode"
          />
        </div>

        <div className="input-group">
          <span className="question">
            <span className="required">Secret Key</span>
            <ToolTip
              toolTip="Issuer secret key generated by the Interswitch tokenization gateway"
              anchor={
                <img
                  alt="info tooltip"
                  src={resources.icons.INFO_CIRCLE_SOLID}
                  className="generic-icon small"
                />
              }
            />
          </span>
          <Input
            value={formData.encryptionSecret}
            onChange={value => handleChange("encryptionSecret", value)}
            placeholder="Enter secret key"
            inputValidation={validations.encryptionSecret}
            id="ctsIssuerSecret"
          />
        </div>

        <div className="input-group">
          <span className="question vertical-margin">
            <span>Send OTP on behalf of issuer?</span>
            <Switch
              checked={formData.sendOtpOnBehalfOfIssuer}
              id="ctsSendOnBehalfOfIssuer"
              onChange={checked =>
                handleChange("sendOtpOnBehalfOfIssuer", checked)
              }
            />
          </span>
        </div>

        {!formData.sendOtpOnBehalfOfIssuer && (
          <div className="input-group">
            <span className="question">Issuer OTP endpoint</span>
            <Input
              value={formData.sendOtpEndpoint}
              onChange={value => handleChange("sendOtpEndpoint", value)}
              placeholder="Enter issuer's OTP endpoint"
              inputValidation={validations.sendOtpEndpoint}
              id="ctsIssuerOtpEndpoint"
            />
          </div>
        )}

        <div className="input-group">
          <span className="question vertical-margin">
            <span className="flex inline spaced">
              <span>Verify external wallet requests only</span>
              <ToolTip
                toolTip="The decision to verify all card digitization requests, as to whether it's coming from the issuer wallet or not"
                position="right"
                anchor={
                  <img
                    alt="info tooltip"
                    src={resources.icons.INFO_CIRCLE_SOLID}
                    className="generic-icon small"
                  />
                }
              />
            </span>
            <Switch
              checked={formData.idvOnExternalWalletsOnly}
              id="ctsExternalWalletsOnly"
              onChange={checked =>
                handleChange("idvOnExternalWalletsOnly", checked)
              }
            />
          </span>
        </div>

        <div className="input-group">
          <span className="question">
            <span>Wallet Provider ID</span>
            <ToolTip
              toolTip="Issuer wallet provider id issued by the Thales team"
              anchor={
                <img
                  alt="info tooltip"
                  src={resources.icons.INFO_CIRCLE_SOLID}
                  className="generic-icon small"
                />
              }
            />
          </span>
          <Input
            value={formData.walletProviderId}
            onChange={value => handleChange("walletProviderId", value)}
            placeholder="Enter wallet provider id"
            inputValidation={validations.walletProviderId}
            id="ctsWalletProviderId"
          />
        </div>

        <div className="input-group">
          <span className="question">
            <span className="required">Max virtual card provisioning</span>
            <ToolTip
              toolTip="The maximum number of virtual card provisioning allowed by the issuer"
              anchor={
                <img
                  alt="info tooltip"
                  src={resources.icons.INFO_CIRCLE_SOLID}
                  className="generic-icon small"
                />
              }
            />
          </span>
          <Input
            value={formData.maxVirtualCardProvisioning.toLocaleString("en-US", {
              useGrouping: false
            })}
            onChange={value => {
              const numericValue = value.replace(/,/g, "");
              handleChange("maxVirtualCardProvisioning", numericValue);
            }}
            placeholder="Enter wallet provider id"
            number
            inputValidation={validations.maxVirtualCardProvisioning}
            id="ctsMaxVCard"
          />
        </div>

        <div className="input-group">
          <span className="question">OTP Message Template</span>
          <TextArea
            value={formData.otpMessageTemplate}
            onChange={value => handleChange("otpMessageTemplate", value)}
            placeholder="Create message template"
            refValue={templateRef}
            inputValidation={validations.otpMessageTemplate}
            id="ctsOtpMessageTemplate"
          />
          <div className="margin-top flex between">
            <Button
              size="small"
              type="accent"
              onClick={insertShortCode}
              className="blue"
              id="ctsOtpInsertButton"
            >
              <strong>INSERT OTP SHORTCODE</strong>
            </Button>
            <span>Count: {formData.otpMessageTemplate.length}</span>
          </div>
        </div>
        <br />

        <div className="input-group">
          <span className="question">
            Additional OTP Parameters
            <ToolTip
              toolTip={
                <span>
                  Other additional request parameters the issuer might require
                  when sending OTP. Must be in an object in{" "}
                  <a
                    target="_blank"
                    href="https://en.wikipedia.org/wiki/JSON#Syntax"
                    rel="noreferrer noopener"
                  >
                    JSON format
                  </a>
                </span>
              }
              anchor={
                <img
                  alt="info tooltip"
                  src={resources.icons.INFO_CIRCLE_SOLID}
                  className="generic-icon small"
                />
              }
            />
          </span>
          <TextArea
            value={formData.sendOtpAdditionalRequestParam}
            onChange={value =>
              handleChange("sendOtpAdditionalRequestParam", value)
            }
            placeholder="Enter parameters JSON"
            inputValidation={validations.sendOtpAdditionalRequestParam}
            id="ctsOtpParams"
          />
        </div>

        <br />
        <br />
        <div className="flex spaced end vertical-margin">
          <Button
            type="accent"
            buttonType="button"
            onClick={() => navigate(-1)}
            id="ctsCancel"
          >
            Cancel
          </Button>
          <Button buttonType="submit" loading={loading} id="ctsSubmit">
            Create on CTS
          </Button>
        </div>
      </form>
    </main>
  );
};

export default OnboardIssuerPage;
